import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["prefixCls", "inputId", "name", "value", "tabIndex", "aria-label", "defaultChecked", "checked", "disabled", "onChange", "children", "className", "indeterminate", "inputProps", "inputRef"];
import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { useControlled, setupGetInstanceId, KEY_VALUES, getText } from '../utils';
import { CheckboxContext } from './context';
var getInstanceId = setupGetInstanceId();
var Checkbox = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _useContext = useContext(CheckboxContext),
    groupValue = _useContext.value,
    nameContext = _useContext.name,
    disabledContext = _useContext.disabled,
    controlled = _useContext.controlled,
    onGroupChange = _useContext.onChange;
  var customizePrefixCls = props.prefixCls,
    inputIdProp = props.inputId,
    _props$name = props.name,
    name = _props$name === void 0 ? nameContext : _props$name,
    value = props.value,
    _props$tabIndex = props.tabIndex,
    tabIndex = _props$tabIndex === void 0 ? 0 : _props$tabIndex,
    ariaLabelProp = props["aria-label"],
    defaultChecked = props.defaultChecked,
    controlledChecked = props.checked,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? disabledContext : _props$disabled,
    onChange = props.onChange,
    children = props.children,
    className = props.className,
    _props$indeterminate = props.indeterminate,
    indeterminate = _props$indeterminate === void 0 ? false : _props$indeterminate,
    inputProps = props.inputProps,
    inputRef = props.inputRef,
    restProps = _objectWithoutPropertiesLoose(props, _excluded);
  var inputId = inputIdProp || "checkbox-" + getInstanceId();
  var isChecked = useCallback(function () {
    if (typeof groupValue !== 'undefined' && typeof value !== 'undefined') {
      return groupValue.some(function (i) {
        return i === value;
      });
    }
    return controlledChecked;
  }, [controlledChecked, groupValue, value]);
  var _useControlled = useControlled(isChecked(), defaultChecked),
    checked = _useControlled[0],
    setChecked = _useControlled[1];
  var _useState = useState(false),
    focused = _useState[0],
    setFocused = _useState[1];
  var prefixCls = customizePrefixCls ? customizePrefixCls + "-checkbox" : 'ap-checkbox';
  var handleChange = useCallback(function (event) {
    if (disabled) {
      return;
    }
    var nextChecked = !checked;
    setChecked(nextChecked);
    onChange === null || onChange === void 0 ? void 0 : onChange(nextChecked, event);
    onGroupChange === null || onGroupChange === void 0 ? void 0 : onGroupChange(value, nextChecked, event);
  }, [disabled, onChange, onGroupChange, setChecked, value, checked]);
  var onFocus = function onFocus() {
    if (inputProps && inputProps.onBlur) {
      inputProps.onBlur.apply(inputProps, arguments);
    }
    setFocused(true);
  };
  var onBlur = function onBlur() {
    if (inputProps && inputProps.onBlur) {
      inputProps.onBlur.apply(inputProps, arguments);
    }
    setFocused(false);
  };
  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === KEY_VALUES.ENTER || event.key === KEY_VALUES.SPACE) {
      handleChange(event);
      event.preventDefault();
    }
  };
  var ariaLabel = ariaLabelProp || getText(children);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: classNames(prefixCls + "-container", className),
    ref: ref,
    onClick: handleChange,
    onKeyDown: handleKeyDown
  }, restProps), /*#__PURE__*/React.createElement("div", {
    className: classNames(prefixCls, disabled && prefixCls + "-disabled", checked && prefixCls + "-checked", focused && prefixCls + "-focus", indeterminate && prefixCls + "-indeterminate")
  }, /*#__PURE__*/React.createElement("div", {
    className: "a-hidden-accessibility"
  }, /*#__PURE__*/React.createElement("input", _extends({
    type: "checkbox",
    role: "checkbox",
    "aria-live": "assertive",
    "aria-label": ariaLabel,
    "aria-disabled": disabled,
    "aria-checked": indeterminate ? 'mixed' : checked,
    id: inputId,
    ref: inputRef,
    name: name,
    value: value,
    tabIndex: tabIndex,
    defaultChecked: controlled ? undefined : checked,
    checked: controlled ? checked : undefined,
    disabled: disabled,
    className: prefixCls + "-input",
    onFocus: onFocus,
    onBlur: onBlur,
    onChange: function onChange() {}
  }, inputProps))), /*#__PURE__*/React.createElement("span", {
    className: "Appkit4-icon icon-checkbox-deselected-fill",
    "aria-hidden": true
  }), /*#__PURE__*/React.createElement("span", {
    className: "icon-checkbox-backdrop-outline",
    "aria-hidden": true
  }, checked && /*#__PURE__*/React.createElement("span", {
    className: "Appkit4-icon icon-checkbox-selected-fill"
  }), indeterminate && /*#__PURE__*/React.createElement("span", {
    className: "Appkit4-icon icon-checkbox-indeterminate-fill"
  }))), children && /*#__PURE__*/React.createElement("label", {
    className: prefixCls + "-label",
    htmlFor: inputId,
    onClick: function onClick(event) {
      return event.preventDefault();
    },
    "aria-hidden": true
  }, children));
});
Checkbox.displayName = "Checkbox";
export default Checkbox;